import { ManagerBase, MouseEventResult } from './manager-base';
import { LevelDesignerComponent } from '../level-designer/level-designer.component';
import { LevelDesignerElementBase } from '../elements/element-base';
import { FormArray, FormGroup, FormControl } from '@angular/forms';
import { ParkingSpaceElement } from '../elements/parking/parkingspace';
import { PointElement } from '../elements/point-element';
import { PolygonElement } from '../elements/polygon-element';
import { v4 as uuidv4 } from 'uuid';

export class ClipboardManager extends ManagerBase {
    private clipboard: LevelDesignerElementBase[] = [];
    private lastMousePosition: { x: number, y: number } | null = null;
    private originalSelectionCenter: { x: number, y: number } | null = null;

    constructor(
        levelDesigner: LevelDesignerComponent,
        canvas: HTMLCanvasElement
    ) {
        super(levelDesigner, canvas);
    }

    public override handleKeyDown(event: KeyboardEvent): boolean {
        // Only handle Ctrl+C and Ctrl+V
        if (!event.ctrlKey) return false;

        const key = event.key.toLowerCase();
        if (key === 'c') {
            event.preventDefault(); // Prevent default browser copy
            this.copySelectedElements();
            return true;
        }
        if (key === 'v') {
            event.preventDefault(); // Prevent default browser paste
            this.pasteElements();
            return true;
        }
        return false;
    }

    public override handleMouseMove(event: MouseEvent): MouseEventResult | undefined {
        const worldCoords = this.levelDesigner.getWorldCoordinates(event);
        this.lastMousePosition = worldCoords;
        return undefined;
    }

    private copySelectedElements(): void {
        if (this.levelDesigner.selectedElements.length === 0) return;

        // Only update the original selection center if we're copying new elements
        // This ensures subsequent pastes maintain the same reference point
        this.originalSelectionCenter = this.calculateElementsCenter(this.levelDesigner.selectedElements);

        // Deep copy selected elements
        this.clipboard = this.levelDesigner.selectedElements.map(element => this.deepCopyElement(element));
    }

    private pasteElements(): void {
        if (this.clipboard.length === 0 || !this.lastMousePosition || !this.originalSelectionCenter) return;
        
        // Calculate the vector from original selection center to cursor position
        const vectorX = this.lastMousePosition.x - this.originalSelectionCenter.x;
        const vectorY = this.lastMousePosition.y - this.originalSelectionCenter.y;

        // Create new elements maintaining their relative positions
        const newElements: LevelDesignerElementBase[] = [];
        this.clipboard.forEach(element => {
            const newElement = this.deepCopyElement(element);
            // Move the element by the vector from original selection center to cursor
            newElement.move(
                vectorX,
                vectorY
            );
            newElements.push(newElement);
        });

        // Sort elements by layer to ensure parents are added before children
        newElements.sort((a, b) => a.layerIndex - b.layerIndex);

        // Create a combined array of existing and new elements for validation
        const combinedElements = [...this.levelDesigner.elements, ...newElements];

        // Validate all elements before adding
        const invalidElements = newElements.filter(element => {
            const validationResult = element.checkPositionValid(combinedElements);
            return validationResult.errors.length > 0;
        });

        if (invalidElements.length > 0) {
            // Show validation errors
            invalidElements.forEach(element => {
                const validationResult = element.checkPositionValid(combinedElements);
                validationResult.errors.forEach(error => {
                    this.levelDesigner.toastService.error(error, 'Paste Error');
                });
            });
            return;
        }

        // Add elements in sorted order to maintain parent-child relationships
        newElements.forEach(element => {
            this.levelDesigner.addElement(element);
        });
        
        // Update selection to new elements
        this.levelDesigner.setSelectedElements(newElements);
        
        // Sort elements and save state
        this.levelDesigner.sortElementsByLayer();
        this.levelDesigner.saveState();
        this.levelDesigner.redraw();
    }

    private calculateElementsCenter(elements: LevelDesignerElementBase[]): { x: number, y: number } {
        if (elements.length === 0) return { x: 0, y: 0 };

        let totalX = 0;
        let totalY = 0;

        elements.forEach(element => {
            const bounds = element.getBoundingBox();
            totalX += bounds.x + bounds.width / 2;
            totalY += bounds.y + bounds.height / 2;
        });

        return {
            x: totalX / elements.length,
            y: totalY / elements.length
        };
    }

    private deepCopyElement(element: LevelDesignerElementBase): LevelDesignerElementBase {
        // Create a new form group with the same structure
        const newFormGroup = new FormGroup({});
        
        // Copy all form controls
        Object.keys(element.formGroup.controls).forEach(key => {
            const control = element.formGroup.get(key);
            if (!control) return;
            
            if (control instanceof FormArray) {
                // Handle FormArray
                const newArray = new FormArray<any>([]);
                control.controls.forEach(arrayControl => {
                    if (arrayControl instanceof FormGroup) {
                        newArray.push(this.deepCopyFormGroup(arrayControl));
                    } else {
                        newArray.push(new FormControl(arrayControl.value));
                    }
                });
                newFormGroup.addControl(key, newArray);
            } else if (control instanceof FormGroup) {
                // Handle nested FormGroup
                newFormGroup.addControl(key, this.deepCopyFormGroup(control));
            } else {
                // Handle regular FormControl
                let value = control.value;
                
                // Special handling for ID fields
                if (key.toLowerCase() === 'id') {
                    if (typeof value === 'string') {
                        // Check if it's a GUID (UUID v4)
                        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
                        if (guidRegex.test(value)) {
                            // Generate new GUID using uuidv4
                            value = uuidv4();
                        }
                    } else if (typeof value === 'number') {
                        // Set numeric IDs to null
                        value = null;
                    }
                }
                
                newFormGroup.addControl(key, new FormControl(value));
            }
        });

        // Create a new instance of the same class
        const elementClass = element.constructor as new (formGroup: FormGroup) => LevelDesignerElementBase;
        const newElement = new elementClass(newFormGroup);

        // Copy all properties that aren't form controls
        Object.keys(element).forEach(key => {
            if (key !== 'formGroup' && key !== 'currentParentElement') {
                (newElement as any)[key] = (element as any)[key];
            }
        });

        // Update the form with the copied values
        newElement.updateForm();

        return newElement;
    }

    private deepCopyFormGroup(formGroup: FormGroup): FormGroup {
        const newFormGroup = new FormGroup({});
        
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);
            if (!control) return;
            
            if (control instanceof FormArray) {
                const newArray = new FormArray<any>([]);
                control.controls.forEach(arrayControl => {
                    if (arrayControl instanceof FormGroup) {
                        newArray.push(this.deepCopyFormGroup(arrayControl));
                    } else {
                        newArray.push(new FormControl(arrayControl.value));
                    }
                });
                newFormGroup.addControl(key, newArray);
            } else if (control instanceof FormGroup) {
                newFormGroup.addControl(key, this.deepCopyFormGroup(control));
            } else {
                newFormGroup.addControl(key, new FormControl(control.value));
            }
        });

        return newFormGroup;
    }

    public override OnDraw(): void {
        // No drawing needed for clipboard manager
    }
} 
